import { createEmailHistoryApiInstance, getForwardedHost } from 'utils/emailHistory/apiUtils';

/**
 * Email History Base API
 * Provides standardized request handling for all email history API endpoints
 */

// Cache for API instances
const apiInstances = new Map();

/**
 * Get or create an API instance for a given base path
 * @param {string} basePath - API base path
 * @returns {axios.AxiosInstance}
 */
function getApiInstance(basePath) {
  if (!apiInstances.has(basePath)) {
    apiInstances.set(basePath, createEmailHistoryApiInstance(basePath));
  }
  return apiInstances.get(basePath);
}

/**
 * Standard headers for API requests
 * @param {string} baseURL - API base URL
 * @returns {Object} Headers object
 */
function getStandardHeaders(baseURL) {
  return {
    'Content-Type': 'application/json;charset=UTF-8',
    'x-forwarded-host': getForwardedHost(baseURL),
  };
}

/**
 * Normalize API error for consistent handling
 * @param {Error} error - Error from API call
 * @returns {Error} Normalized error
 */
export function normalizeApiError(error) {
  // Check for error response with code
  if (error.response?.data?.error) {
    const apiError = error.response.data.error;
    const normalizedError = new Error(apiError.message || 'Request failed');
    normalizedError.code = apiError.code;
    normalizedError.status = error.response.status;
    return normalizedError;
  }
  
  // Check for network error
  if (error.message === 'Network Error') {
    const networkError = new Error('Network error. Please check your connection.');
    networkError.code = 'NETWORK_ERROR';
    return networkError;
  }
  
  // Return original error with fallback message
  if (!error.message) {
    error.message = 'An unexpected error occurred';
  }
  
  return error;
}

/**
 * Make a GET request to the email history API
 * @param {string} basePath - API base path (e.g., '/api3.2/emailHistory')
 * @param {string} endpoint - API endpoint (e.g., '/campaigns')
 * @param {Object} [params] - Query parameters
 * @returns {Promise<any>} Response data
 */
export async function apiGet(basePath, endpoint, params = {}) {
  const api = getApiInstance(basePath);
  
  // Build query string
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryParams.set(key, String(value));
    }
  });
  
  const queryString = queryParams.toString();
  const url = queryString ? `${endpoint}?${queryString}` : endpoint;
  
  try {
    const { data } = await api.get(url, {
      headers: getStandardHeaders(api.defaults.baseURL),
    });
    
    if (!data.success) {
      throw new Error(data.error?.message || 'Request failed');
    }
    
    return data.data;
  } catch (error) {
    console.error(`Error fetching ${endpoint}:`, error);
    throw normalizeApiError(error);
  }
}

/**
 * Make a POST request to the email history API
 * @param {string} basePath - API base path (e.g., '/api3.2/emailHistory')
 * @param {string} endpoint - API endpoint
 * @param {Object} [body] - Request body
 * @returns {Promise<any>} Response data
 */
export async function apiPost(basePath, endpoint, body = {}) {
  const api = getApiInstance(basePath);
  
  try {
    const { data } = await api.post(endpoint, body, {
      headers: getStandardHeaders(api.defaults.baseURL),
    });
    
    if (!data.success) {
      throw new Error(data.error?.message || 'Request failed');
    }
    
    return data.data;
  } catch (error) {
    console.error(`Error posting to ${endpoint}:`, error);
    throw normalizeApiError(error);
  }
}

/**
 * Make a PUT request to the email history API
 * @param {string} basePath - API base path
 * @param {string} endpoint - API endpoint
 * @param {Object} [body] - Request body
 * @returns {Promise<any>} Response data
 */
export async function apiPut(basePath, endpoint, body = {}) {
  const api = getApiInstance(basePath);
  
  try {
    const { data } = await api.put(endpoint, body, {
      headers: getStandardHeaders(api.defaults.baseURL),
    });
    
    if (!data.success) {
      throw new Error(data.error?.message || 'Request failed');
    }
    
    return data.data;
  } catch (error) {
    console.error(`Error updating ${endpoint}:`, error);
    throw normalizeApiError(error);
  }
}

/**
 * Make a DELETE request to the email history API
 * @param {string} basePath - API base path
 * @param {string} endpoint - API endpoint
 * @returns {Promise<any>} Response data
 */
export async function apiDelete(basePath, endpoint) {
  const api = getApiInstance(basePath);
  
  try {
    const { data } = await api.delete(endpoint, {
      headers: getStandardHeaders(api.defaults.baseURL),
    });
    
    if (!data.success) {
      throw new Error(data.error?.message || 'Request failed');
    }
    
    return data.data;
  } catch (error) {
    console.error(`Error deleting ${endpoint}:`, error);
    throw normalizeApiError(error);
  }
}

// Pre-configured API paths
export const API_PATHS = {
  EMAIL_HISTORY: '/api3.2/emailHistory',
  CANMAIL: '/api3.2/canmail',
};

/**
 * Email History API convenience methods
 */
export const emailHistoryApi = {
  get: (endpoint, params) => apiGet(API_PATHS.EMAIL_HISTORY, endpoint, params),
  post: (endpoint, body) => apiPost(API_PATHS.EMAIL_HISTORY, endpoint, body),
  put: (endpoint, body) => apiPut(API_PATHS.EMAIL_HISTORY, endpoint, body),
  delete: (endpoint) => apiDelete(API_PATHS.EMAIL_HISTORY, endpoint),
};

/**
 * CANmail API convenience methods
 */
export const canmailApi = {
  get: (endpoint, params) => apiGet(API_PATHS.CANMAIL, endpoint, params),
  post: (endpoint, body) => apiPost(API_PATHS.CANMAIL, endpoint, body),
  put: (endpoint, body) => apiPut(API_PATHS.CANMAIL, endpoint, body),
  delete: (endpoint) => apiDelete(API_PATHS.CANMAIL, endpoint),
};

